// src/hooks/brandCustomization/useTicketCustomization.js
import { useState, useEffect } from 'react';
import ticketCustomizationService from '../../helpers/ticketCustomizationHelper';
import { defaultTicketTexts, defaultTicketStyles } from '../../utils/brandCustomization';
import Swal from 'sweetalert2';
import TicketViewContent from '../../components/public/TicketViewContent';

/**
 * Hook para manejar la personalización de la tarjeta de puntos
 * @param {string} marcaId - ID de la marca
 * @returns {object} Estado y funciones para manejar ticket customization
 */
export const useTicketCustomization = (marcaId) => {
    const [formData, setFormData] = useState({
        texts: defaultTicketTexts,
        styles: defaultTicketStyles,
        layout: 'wallet',
        layoutOptions: {
            showLogo: true,
            showSlogan: true,
            showBrandName: true,
            showUserInfo: true,
            showQRInHeader: true,
            showQRCode: true,
            showPointsSection: true,
            showStars: true,
            showProgressBar: true,
            showRewardsIndicator: true,
            showDivider: true,
            showClientInfo: true,
            showEmail: true,
            showReferralCode: true,
            showStoreInfo: true,
            showMapsLink: true,
            showFooter: true,
            showCreatedDate: true,
            showShareButton: true,
            showWalletPattern: true
        },
        heroImage: null,
        isActive: true
    });
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Cargar datos del backend
    const fetchData = async () => {
        if (!marcaId) return;

        setLoading(true);
        try {
            const ticketData = await ticketCustomizationService.getCustomization(marcaId);

            if (ticketData) {
                setFormData({
                    texts: {
                        ...defaultTicketTexts,
                        ...(ticketData.texts || {})
                    },
                    styles: {
                        ...defaultTicketStyles,
                        ...(ticketData.styles || {})
                    },
                    layout: ticketData.layout || 'wallet',
                    layoutOptions: {
                        showLogo: true,
                        showSlogan: true,
                        showBrandName: true,
                        showUserInfo: true,
                        showQRInHeader: true,
                        showQRCode: true,
                        showPointsSection: true,
                        showStars: true,
                        showProgressBar: true,
                        showRewardsIndicator: true,
                        showDivider: true,
                        showClientInfo: true,
                        showEmail: true,
                        showReferralCode: true,
                        showStoreInfo: true,
                        showMapsLink: true,
                        showFooter: true,
                        showCreatedDate: true,
                        showShareButton: true,
                        showWalletPattern: true,
                        ...(ticketData.layoutOptions || {})
                    },
                    heroImage: ticketData.heroImage || null,
                    isActive: ticketData.isActive !== false
                });
            }
        } catch (error) {
            if (error.status !== 404) {
                console.error('Error cargando personalización de tarjeta:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo cargar la personalización de la tarjeta',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [marcaId]);

    // Actualizar un campo específico
    const updateField = (field, value) => {
        setFormData(prev => {
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return {
                    ...prev,
                    [parent]: {
                        ...prev[parent],
                        [child]: value
                    }
                };
            }

            return {
                ...prev,
                [field]: value
            };
        });
    };

    // Toggle activación
    const toggleActive = async () => {
        const currentActive = formData.isActive;
        const newActive = !currentActive;

        updateField('isActive', newActive);

        try {
            await ticketCustomizationService.updateCustomization(marcaId, {
                isActive: newActive
            });

            Swal.fire({
                title: newActive ? 'Personalización Activada' : 'Personalización Desactivada',
                text: newActive
                    ? 'Las tarjetas usarán la personalización personalizada'
                    : 'Las tarjetas usarán los valores por defecto',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            updateField('isActive', currentActive);

            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo actualizar el estado',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Guardar personalización
    const save = async () => {
        setIsSaving(true);

        try {
            const customizationData = {
                texts: formData.texts,
                styles: formData.styles,
                layout: formData.layout,
                layoutOptions: formData.layoutOptions,
                heroImage: formData.heroImage,
                isActive: formData.isActive
            };

            try {
                await ticketCustomizationService.updateCustomization(marcaId, customizationData);
            } catch (updateError) {
                if (updateError.status === 404) {
                    await ticketCustomizationService.createCustomization(marcaId, customizationData);
                } else {
                    throw updateError;
                }
            }

            Swal.fire({
                title: '¡Guardado!',
                text: 'La personalización se guardó correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            await fetchData();
        } catch (error) {
            console.error('Error guardando personalización:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo guardar la personalización',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Resetear
    const reset = async () => {
        const result = await Swal.fire({
            title: '¿Resetear Personalización?',
            text: 'Se perderán todos los cambios personalizados',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, resetear',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            await ticketCustomizationService.deleteCustomization(marcaId);

            Swal.fire({
                title: '¡Reseteado!',
                text: 'Se restauraron los valores por defecto',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            await fetchData();
        } catch (error) {
            console.error('Error reseteando personalización:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo resetear la personalización',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Obtener solo el heroImage sin afectar el resto del estado
    // Útil después de subir/eliminar imagen hero para preservar cambios locales
    const fetchHeroImageOnly = async () => {
        if (!marcaId) return;

        try {
            const ticketData = await ticketCustomizationService.getCustomization(marcaId);
            if (ticketData?.heroImage !== undefined) {
                updateField('heroImage', ticketData.heroImage || null);
            }
        } catch (error) {
            // Si no existe la personalización, el heroImage es null
            if (error.status === 404) {
                updateField('heroImage', null);
            } else {
                console.error('Error obteniendo heroImage:', error);
            }
        }
    };

    // Preview - Genera componente React para preview (igual que el original)
    const preview = (marca) => {
        // Datos de muestra para el ticket (para preview) - igual que en el original
        const sampleTicket = {
            _id: 'sample-ticket',
            puntos: 5,
            tipoFidelizacion: 'PUNTOS', // Puede ser 'PUNTOS' o acumulación
            marca: {
                _id: marca?._id || 'sample-marca',
                nombre: marca?.nombre || 'Tu Marca',
                eslogan: marca?.eslogan || 'Tu eslogan aquí',
                logotipo: marca?.logotipo || null,
                puntosTicket: 10,
                recompensas: []
            },
            cliente: {
                nombre: 'Juan',
                apellido: 'Pérez',
                email: 'juan@example.com',
                codigoReferido: 'REF123'
            },
            tienda: {
                nombre: 'Tienda Centro',
                via: 'Calle',
                calle: 'Ejemplo',
                numero: '123',
                poblacion: 'Ciudad'
            },
            created_at: new Date().toISOString()
        };

        // Usar exactamente el mismo componente que TicketView.jsx
        const component = (
            <TicketViewContent
                ticket={sampleTicket}
                texts={formData.texts}
                styles={formData.styles}
                layout={formData.layout}
                layoutOptions={formData.layoutOptions}
                heroImage={formData.heroImage}
                isPreview={true}
            />
        );

        return component;
    };

    return {
        formData,
        loading,
        isSaving,
        updateField,
        toggleActive,
        save,
        reset,
        fetchData,
        fetchHeroImageOnly,
        preview
    };
};

export default useTicketCustomization;

